import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { usePlatform } from '@localagent/platform';
import { APIError, uuidV4, type WorkspaceProjection } from '@localagent/sdk';
import {
  useCreateCloudWorkspace,
  useRegisterWorkspace,
  useArchiveWorkspace,
  useWorkspaces,
} from '../hooks/useWorkspaces';
import {
  launchedWorkspaceIdsAtom,
  launchWorkspaceAction,
  dismissWorkspaceAction,
} from '../stores/workspace/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { toast } from '@localagent/ui/components/sonner';
import { Button } from '@localagent/ui/components/button';
import { DataList, TrashIcon } from '@localagent/ui/components/data-list';
import { ListAddMenu } from '@localagent/ui/components/list-filter-menu';
import { useFilteredList } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import { PageShell } from '../components/layout/PageShell';
import { workspaceArchiveFailureMessage } from './workspaceArchiveFailure';
import { WorkspaceArchiveTeamsNotice } from '../components/workspaces/WorkspaceArchiveTeamsNotice';
import { useTeams } from '../hooks/useWorkManagement';
import { WorkspaceResourceSummary } from '../components/workspaces/WorkspaceResourceCards';
import { WorkspaceReadiness } from '../components/workspaces/WorkspaceReadiness';
import { useExperienceMode } from '../hooks/useExperienceMode';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

function PlayIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

const WORKSPACE_TYPE = {
  local: { label: 'Local', hint: 'Files live on the assigned local device' },
  cloud: { label: 'Cloud', hint: 'Files live in managed cloud storage' },
} as const;

function workspacePath(workspace: WorkspaceProjection): string {
  return workspace.binding?.folder_path ?? '';
}

export function WorkspacesView() {
  useCatalogSubscription('workspaces');
  const navigate = useNavigate();
  const { data: workspaces, isLoading, error } = useWorkspaces();
  const launchedIds = useAtomValue(launchedWorkspaceIdsAtom);
  const sdkReady = useAtomValue(sdkConfiguredAtom);
  const launchWorkspace = useSetAtom(launchWorkspaceAction);
  const dismissWorkspace = useSetAtom(dismissWorkspaceAction);
  const registerWorkspace = useRegisterWorkspace();
  const createCloudWorkspace = useCreateCloudWorkspace();
  const archiveWorkspace = useArchiveWorkspace();
  const teamsQuery = useTeams();
  const platform = usePlatform();
  const isLaunched = (id: string) => launchedIds.includes(id);

  const [createForm, setCreateForm] = useState<'local' | 'cloud' | null>(null);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [cloudCreationError, setCloudCreationError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [archiveFailure, setArchiveFailure] = useState<{
    workspaceId: string;
    message: string;
  } | null>(null);
  const canCreateCloudFromFolder = platform.supportsLocalDirectories && sdkReady;
  const { mode } = useExperienceMode();
  const showAdvancedChrome = mode === 'advanced';
  const showNormalStatus = mode === 'normal';

  const workspaceSearchFn = (ws: WorkspaceProjection, query: string) => {
    const q = query.toLowerCase();
    return ws.name.toLowerCase().includes(q) || workspacePath(ws).toLowerCase().includes(q);
  };

  // Sort newest-first so freshly registered workspaces always appear on
  // page 1 of the paginated list (important for e2e/storybook tests that
  // create a workspace then expect to see it immediately).
  const sortedWorkspaces = [...(workspaces ?? [])]
    .filter((workspace) => workspace.lifecycle !== 'archived')
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

  const { filteredItems, searchValue, onSearchChange } = useFilteredList({
    items: sortedWorkspaces,
    searchFn: workspaceSearchFn,
  });

  const pagination = usePagination({
    totalItems: filteredItems.length,
    pageSize: 50,
    pageSizeOptions: [20, 50, 100, 200],
  });

  // Reset to first page when search changes
  useEffect(() => {
    pagination.reset();
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedItems = filteredItems.slice(pagination.startIndex, pagination.endIndex);

  const resetCreateForm = () => {
    setCreateForm(null);
    setSelectedPath('');
    setWorkspaceName('');
    setCloudCreationError(null);
  };

  const openCreateForm = (kind: 'local' | 'cloud') => {
    setCreateForm(kind);
    setSelectedPath('');
    setWorkspaceName('');
    setCloudCreationError(null);
  };

  const handleSelectDirectory = async () => {
    try {
      const path = await platform.selectDirectory();
      if (path) {
        setSelectedPath(path);
        // Extract directory name as default workspace name if not set
        if (!workspaceName) {
          const dirName = path.split('/').pop() || path.split('\\').pop() || '';
          setWorkspaceName(dirName);
        }
      }
    } catch (error) {
      console.error('Failed to select directory:', error);
      toast.error('Failed to open directory picker');
    }
  };

  const handleRegisterWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPath) {
      toast.error('Please select a workspace directory');
      return;
    }

    try {
      await registerWorkspace.mutateAsync({
        workspaceRoot: selectedPath || workspaceName,
        name: workspaceName || undefined,
        idempotencyKey: uuidV4(),
      });

      toast.success('Workspace registered successfully');
      resetCreateForm();
    } catch (error) {
      console.error('Failed to register workspace:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to register workspace');
    }
  };

  const handleCreateCloudWorkspace = async (sourceRoot?: string) => {
    setCloudCreationError(null);
    try {
      const projection = await createCloudWorkspace.mutateAsync({
        name: sourceRoot ? workspaceName || undefined : undefined,
        sourceRoot,
        idempotencyKey: uuidV4(),
      });
      toast.success('Cloud workspace created');
      resetCreateForm();
      void navigate(`/workspaces/${projection.workspace_id}?tab=overview`);
    } catch (error) {
      const isSourcePreflightRefusal =
        Boolean(sourceRoot) &&
        error instanceof APIError &&
        (error.code.startsWith('CLOUD_SOURCE_') || error.code === 'INVALID_CLOUD_SOURCE');
      if (!isSourcePreflightRefusal) {
        console.error('Failed to create cloud workspace:', error);
      }
      let message = error instanceof Error ? error.message : 'Failed to create cloud workspace';
      if (isSourcePreflightRefusal && !message.toLowerCase().includes('local workspace')) {
        message = `${message}. Keep this folder as a local workspace or choose a smaller folder.`;
      }
      if (sourceRoot) setCloudCreationError(message);
      toast.error(message);
    }
  };

  const handleCreateCloudFromFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPath) {
      toast.error('Please select a workspace directory');
      return;
    }
    await handleCreateCloudWorkspace(selectedPath);
  };

  if (isLoading) {
    return (
      <LoadingState label="Loading workspaces..." className="h-full" />
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Failed to load workspaces</p>
          <p className="mt-2 text-body text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <PageShell
    >
      <>
        {createForm && (
          <form
            onSubmit={(e) => {
              if (createForm === 'cloud') void handleCreateCloudFromFolder(e);
              else void handleRegisterWorkspace(e);
            }}
            className="mb-6 rounded-lg bg-card p-6"
            data-testid="workspace-create-form"
          >
            <h2 className="mb-4 text-title-2 font-semibold">
              {createForm === 'cloud'
                ? 'Create Cloud Workspace from Folder'
                : 'Register New Workspace'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-label font-medium">
                  Workspace Directory <span className="text-destructive">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={selectedPath}
                    onChange={(e) => setSelectedPath(e.target.value)}
                    placeholder="No directory selected"
                    data-testid="workspace-path-input"
                    className="flex-1 rounded-md bg-muted px-3 py-2 text-body"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void handleSelectDirectory();
                    }}
                    data-testid="browse-directory-button"
                  >
                    Browse...
                  </Button>
                </div>
                <p className="mt-1 text-caption text-muted-foreground">
                  Select the root directory or paste its absolute path
                </p>
              </div>

              <div>
                <label className="mb-2 block text-label font-medium">Workspace Name (optional)</label>
                <input
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="My Workspace"
                  data-testid="workspace-name-input"
                  className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="mt-1 text-caption text-muted-foreground">
                  If not provided, the directory name will be used
                </p>
              </div>

              {cloudCreationError && (
                <p
                  role="alert"
                  data-testid="cloud-folder-create-error"
                  className="rounded-md bg-destructive/10 px-3 py-2 text-body text-destructive"
                >
                  {cloudCreationError}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetCreateForm();
                  }}
                  disabled={registerWorkspace.isPending || createCloudWorkspace.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !selectedPath || registerWorkspace.isPending || createCloudWorkspace.isPending
                  }
                  data-testid={
                    createForm === 'cloud'
                      ? 'submit-create-cloud-from-folder'
                      : 'submit-register-workspace'
                  }
                >
                  {createForm === 'cloud'
                    ? createCloudWorkspace.isPending
                      ? 'Creating cloud workspace…'
                      : 'Create Cloud Workspace'
                    : registerWorkspace.isPending
                      ? 'Registering...'
                      : 'Register Workspace'}
                </Button>
              </div>
            </div>
          </form>
        )}

        <div data-testid="workspaces-list" data-experience-mode={mode}>
          <DataList<WorkspaceProjection>
            items={pagedItems}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder="Search workspaces..."
            headerActions={
              <ListAddMenu
                label="New workspace"
                testId="workspace-new-menu"
                items={[
                  {
                    label: createCloudWorkspace.isPending ? 'Creating cloud workspace…' : 'Empty cloud workspace',
                    testId: 'create-empty-cloud-workspace',
                    disabled: createCloudWorkspace.isPending,
                    onClick: () => {
                      void handleCreateCloudWorkspace();
                    },
                  },
                  ...(canCreateCloudFromFolder
                    ? [
                        {
                          label: 'Cloud from folder',
                          testId: 'create-cloud-from-folder',
                          disabled: createCloudWorkspace.isPending,
                          onClick: () => openCreateForm('cloud'),
                        },
                      ]
                    : []),
                  ...(platform.type === 'desktop'
                    ? [
                        {
                          label: createForm === 'local' ? 'Cancel registration' : 'Register local folder',
                          testId: 'register-workspace-button',
                          onClick: () => {
                            if (createForm === 'local') resetCreateForm();
                            else openCreateForm('local');
                          },
                        },
                      ]
                    : []),
                ]}
              />
            }
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            totalItems={filteredItems.length}
            pageSize={pagination.pageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            onPageSizeChange={pagination.onPageSizeChange}
            getItemId={(ws) => ws.workspace_id}
            itemActions={(workspace) =>
              pendingDeleteId === workspace.workspace_id
                ? []
                : [
                    // Launch/Dismiss is an explicit action (app-shell ADR-003): row click
                    // navigates to the detail page and never mutates launch state.
                    isLaunched(workspace.workspace_id)
                      ? {
                          label: 'Dismiss',
                          icon: <EyeOffIcon />,
                          onClick: () => dismissWorkspace(workspace.workspace_id),
                        }
                      : {
                          label: 'Launch',
                          icon: <PlayIcon />,
                          onClick: () => launchWorkspace(workspace.workspace_id),
                        },
                    ...(workspace.actions.archive.available
                      ? [
                          {
                            label: 'Archive',
                            testId: `workspace-archive-${workspace.workspace_id}`,
                            icon: <TrashIcon />,
                            variant: 'destructive' as const,
                            onClick: () => setPendingDeleteId(workspace.workspace_id),
                          },
                        ]
                      : []),
                  ]
            }
            renderItem={(workspace) => {
              const wsLaunched = isLaunched(workspace.workspace_id);
              const isDeleting = pendingDeleteId === workspace.workspace_id;
              return (
                <div
                  data-testid={`workspace-item-${workspace.workspace_id}`}
                  data-active={wsLaunched}
                  className={`cursor-pointer rounded-lg p-4 transition-colors ${
                    wsLaunched ? 'bg-accent' : 'hover:bg-accent'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium">{workspace.name}</h3>
                      {/* Shown for EVERY workspace: badging only the non-local
                          ones left the most common type unlabelled. */}
                      <span
                        data-testid={`workspace-type-${workspace.workspace_id}`}
                        title={WORKSPACE_TYPE[workspace.assignment.routing_class].hint}
                        className="rounded bg-info-subtle px-1.5 py-0.5 text-caption font-medium text-info-text"
                      >
                        {WORKSPACE_TYPE[workspace.assignment.routing_class].label}
                      </span>
                      {showAdvancedChrome ? (
                        <span
                          data-testid="workspace-lifecycle-pill"
                          className="rounded bg-muted px-1.5 py-0.5 text-caption font-medium"
                        >
                          {workspace.lifecycle}
                        </span>
                      ) : null}
                      {showNormalStatus ? <WorkspaceReadiness projection={workspace} /> : null}
                      {wsLaunched && (
                        <span
                          data-testid={`workspace-launched-pill-${workspace.workspace_id}`}
                          className="rounded-full bg-primary px-2 py-0.5 text-caption font-medium text-primary-foreground"
                        >
                          Launched
                        </span>
                      )}
                    </div>
                    {workspace.assignment.routing_class === 'local' && workspacePath(workspace) && (
                      <p className="mt-1 text-body text-muted-foreground">
                        {workspacePath(workspace)}
                      </p>
                    )}
                    {showAdvancedChrome ? (
                      <WorkspaceResourceSummary projection={workspace} className="mt-2" />
                    ) : null}
                    <p className="mt-2 text-caption text-muted-foreground">
                      Created: {new Date(workspace.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {!workspace.actions.archive.available && (
                    <p
                      className="mt-3 text-body text-destructive"
                      data-testid={`workspace-archive-refusal-${workspace.workspace_id}`}
                    >
                      {workspace.actions.archive.refusal.message}
                    </p>
                  )}
                  {archiveFailure?.workspaceId === workspace.workspace_id && (
                    <p
                      className="mt-3 text-body text-destructive"
                      data-testid={`workspace-archive-api-refusal-${workspace.workspace_id}`}
                    >
                      {archiveFailure.message}
                    </p>
                  )}
                  {isDeleting && workspace.actions.archive.available && (
                    <div className="mt-3 flex items-center justify-between rounded bg-destructive/10 px-3 py-2">
                      <div className="space-y-1">
                        <span className="text-body text-destructive">Archive this workspace?</span>
                        <WorkspaceArchiveTeamsNotice
                          teams={teamsQuery.data?.filter((team) => team.workspace_id === workspace.workspace_id)}
                          error={teamsQuery.error}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDeleteId(null);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={archiveWorkspace.isPending || teamsQuery.data === undefined || teamsQuery.error !== null}
                          data-testid={`confirm-delete-${workspace.workspace_id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchiveFailure(null);
                            archiveWorkspace.mutate(
                              { workspaceId: workspace.workspace_id, idempotencyKey: uuidV4() },
                              {
                                onSuccess: () => {
                                  setArchiveFailure(null);
                                  setPendingDeleteId(null);
                                  toast.success(
                                    `Archive started for workspace "${workspace.name}"`
                                  );
                                },
                                onError: (archiveError) => {
                                  const message = workspaceArchiveFailureMessage(archiveError);
                                  setArchiveFailure({
                                    workspaceId: workspace.workspace_id,
                                    message,
                                  });
                                  toast.error(message);
                                },
                              }
                            );
                          }}
                        >
                          {archiveWorkspace.isPending ? 'Archiving...' : 'Archive'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
            onItemClick={(workspace) => void navigate(`/workspaces/${workspace.workspace_id}`)}
            emptyState={
              !workspaces || workspaces.length === 0 ? (
                <div className="text-center">
                  <p className="text-muted-foreground">No workspaces registered</p>
                  {platform.type === 'desktop' ? (
                    <Button className="mt-4" onClick={() => openCreateForm('local')}>
                      Register Your First Workspace
                    </Button>
                  ) : (
                    <p className="mt-2 text-body text-muted-foreground">
                      Create an empty cloud workspace to get started.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">No workspaces match your search.</p>
              )
            }
          />
        </div>
      </>
    </PageShell>
  );
}
